import { trackByIndex } from 'src/app/shared/utils/track-by.util';
import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { getNamedEnum } from 'src/app/_helpers/enum-helper';
import { CalibrationFrequency } from 'src/app/enums/calibrationFrequency.enum';
import { EquipmentStatus } from 'src/app/enums/equipmentStatus.enum';
import { SampleTypeEnum } from 'src/app/enums/sampleType.enum';
import { CalibrationDialogComponent } from 'src/app/modals/calibration-dialog/calibration-dialog.component';
import { CalibrationEquipment } from 'src/app/models/calibrationEquipment.model';
import { CalibrationEquipmentService } from 'src/app/services/calibration-equipment.service';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { ToastService } from 'src/app/services/toast.service';
import { calibrationDetailsRouteConfig } from 'src/app/shared/constants/calibrationDetailsRouteConfig.constants';
import { GridPermissions } from 'src/app/shared/grid/core-grid/gridPermissions.enum';
import { RolePermissionObjects } from 'src/app/shared/constants/rolePermissionObjects.constants';

export interface KeyValue { label: string; value: string | number; };

@Component({
  standalone: false,
  selector: 'app-calibration-details',
  templateUrl: './calibration-details.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './calibration-details.component.scss'
})
export class CalibrationDetailsComponent implements OnInit {
  tabs: { routerLink: string; title: string; }[];
  overviewFields: KeyValue[];
  readonly permissionObject = RolePermissionObjects.CalibrationEquipment;
  readonly editAction = GridPermissions.Update;
  maintenanceFields: KeyValue[];
  calibrationDetails: CalibrationEquipment;

  constructor(private activatedRoute: ActivatedRoute,
    private calibrationEquipmentService: CalibrationEquipmentService,
    private toastService: ToastService,
    private dialog: MatDialog,
    private dateFormatService: DateFormatService,
  ) { }

  ngOnInit(): void {
    const calibrationId = this.activatedRoute.snapshot.paramMap.get('id');
    if (!calibrationId) {
      this.toastService.showError('Invalid calibration id');
      return;
    }
    this.tabs = calibrationDetailsRouteConfig(calibrationId);
    this.getCalibrationDetails(calibrationId);
  }

  getCalibrationDetails(calibrationId: string) {
    const getDate = (date) => this.dateFormatService.utcToLocalDateAndTime(date);
    this.calibrationEquipmentService.getById(calibrationId).subscribe({
      next: (res) => {
        this.calibrationDetails = res;
        const equipmentStatus = res.status != null ? getNamedEnum(EquipmentStatus)[res.status] : '-';
        this.overviewFields = [
          { label: 'Equipment Code', value: res.equipmentCode ?? '-' },
          { label: 'Model Number', value: res.modelNumber ?? '-' },
          { label: 'Serial Number', value: res.serialNumber ?? '-' },
          { label: 'Manufacturer', value: res.manufacturer ?? '-' },
          { label: 'Status', value: equipmentStatus },
          { label: 'Calibration Equipment Type', value: res.sampleType != null ? SampleTypeEnum[res.sampleType] : '-' },
          { label: 'Date Acquired', value: res.dateAcquired ? getDate(res.dateAcquired).date : '-' },
          { label: 'UUID', value: res.uuid ?? '-' },
          { label: 'Equipment Owners', value: res.owners?.length ? res.owners.map(o => o.ownerFullName || o.ownerUserName).join(', ') : '-' },
        ];
        this.maintenanceFields = [
          { label: 'Last Calibrated', value: res.lastCalibrated ? getDate(res.lastCalibrated).date : '-' },
          { label: 'Calibrated Due', value: res.calibrationDue ? getDate(res.calibrationDue).date : '-' },
          { label: 'Calibration Frequency', value: res.calibrationFrequency != null ? CalibrationFrequency[res.calibrationFrequency] : '-' },
          { label: 'Notify Before Due Days', value: res.notifyBeforeDueDays != null ? res.notifyBeforeDueDays : '-' },
          { label: 'Last Calibration Notification Sent', value: res.lastDueSoonNotificationSentAt ? getDate(res.lastDueSoonNotificationSentAt).date : '-' },
        ];
      },
      error: (error) => {
        this.toastService.showError(error);
      }
    })
  }

  openAddCalibrationPopup() {
    this.dialog.open(CalibrationDialogComponent, {
      data: { calibrationDetails: this.calibrationDetails },
      width: '460px',
      autoFocus: false
    })
  }

  public trackByIndex = trackByIndex;
}
